import * as THREE from "three";
import { applyClockTransform, applySkewToMatrix } from "./TransformUtils";

const DEG = Math.PI / 180;

export function getAndroidLineSpacingShift(lineSpacing, fontSizePx) {
  return (lineSpacing ?? 0) * fontSizePx * 0.15;
}

/**
 * Return an alphabetic baseline that centres the font metrics on centerY.
 * Android's clock renderer uses: centerY - (ascent + descent) / 2.
 * Canvas exposes ascent/descent as positive distances, so the equivalent is
 * centerY + (ascent - descent) / 2.
 */
function androidCenteredBaseline(ctx, text, centerY) {
  const metrics = ctx.measureText(text || "0");
  const ascent = metrics.fontBoundingBoxAscent;
  const descent = metrics.fontBoundingBoxDescent;

  if (Number.isFinite(ascent) && Number.isFinite(descent)) {
    return centerY + (ascent - descent) / 2;
  }

  // Older browsers may not expose font bounding-box metrics. Actual bounds are
  // the closest available fallback while keeping the requested anchor centred.
  const actualAscent = metrics.actualBoundingBoxAscent;
  const actualDescent = metrics.actualBoundingBoxDescent;
  if (Number.isFinite(actualAscent) && Number.isFinite(actualDescent)) {
    return centerY + (actualAscent - actualDescent) / 2;
  }

  return centerY;
}

/**
 * Approximate Android Paint.FontMetrics for the vertical hour/minute layout.
 * fontBoundingBox* supplies the full font line box; emHeight* supplies the
 * inner em box used to estimate Android's top/bottom font-padding imbalance.
 */
function androidVerticalMetrics(ctx, text) {
  const metrics = ctx.measureText(text || "0");
  const ascent = Number.isFinite(metrics.fontBoundingBoxAscent)
    ? metrics.fontBoundingBoxAscent
    : metrics.actualBoundingBoxAscent;
  const descent = Number.isFinite(metrics.fontBoundingBoxDescent)
    ? metrics.fontBoundingBoxDescent
    : metrics.actualBoundingBoxDescent;

  const safeAscent = Number.isFinite(ascent) ? ascent : 0;
  const safeDescent = Number.isFinite(descent) ? descent : 0;
  const emAscent = Number.isFinite(metrics.emHeightAscent)
    ? metrics.emHeightAscent
    : safeAscent;
  const emDescent = Number.isFinite(metrics.emHeightDescent)
    ? metrics.emHeightDescent
    : safeDescent;

  const extraTop = safeAscent - emAscent;
  const extraBottom = safeDescent - emDescent;

  return {
    lineHeight: safeAscent + safeDescent,
    // Mirrors Android: ((extraTop - extraBottom) / 2) * 1.5.
    paddingCompensation: ((extraTop - extraBottom) / 2) * 1.5,
  };
}

// ─── Font loading ─────────────────────────────────────────────────────────────

async function ensureFont(fontFamily, fontSize) {
  if (!fontFamily || fontFamily === "monospace") return fontFamily;
  // Each API font is a distinct uploaded face (including faces named "Bold").
  // Do not request synthetic 700 weight on top of the actual font file.
  const spec = `${fontSize}px "${fontFamily}"`;
  await document.fonts.load(spec);
  await document.fonts.ready;
  return document.fonts.check(spec) ? fontFamily : "monospace";
}

// ─── Date string ─────────────────────────────────────────────────────────────

export function buildDateStr(format, targetDate) {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const sd = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const sm = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const now = targetDate instanceof Date && !isNaN(targetDate.getTime()) ? targetDate : new Date();
  const d = now.getDate().toString().padStart(2, "0");
  const m = (now.getMonth() + 1).toString().padStart(2, "0");
  const y = now.getFullYear();
  const day = days[now.getDay()];
  const shortDay = sd[now.getDay()];
  const month = months[now.getMonth()];
  const shortMonth = sm[now.getMonth()];
  switch (format) {
    case "Day Date Month": return `${shortDay} ${d} ${shortMonth}`;
    case "Day Month Year": return `${d} ${shortMonth} ${y}`;
    case "Full Date": return `${day}, ${month} ${d}, ${y}`;
    case "Short Date": return `${shortMonth} ${d}, ${y}`;
    case "MM/DD/YYYY": return `${m}/${d}/${y}`;
    case "DD/MM/YYYY": return `${d}/${m}/${y}`;
    case "YYYY-MM-DD": return `${y}-${m}-${d}`;
    case "Month Day": return `${month} ${d}`;
    case "Day Month": return `${d} ${month}`;
    case "Short M/D": return `${shortMonth} ${d}`;
    case "Day Only": return day;
    case "Date Only": return d;
    default: return `${shortDay} ${d} ${shortMonth}`;
  }
}

// ─── Canvas text painting ─────────────────────────────────────────────────────

/**
 * Paint clock text onto a canvas that is exactly canvasW × canvasH.
 * Both back and front layers share identical canvas dimensions and text origin.
 * The isFrontLayer flag controls WHICH characters are painted (hours or minutes).
 *
 * Strategy:
 *   1. Measure the full time string as one layout.
 *   2. Measure the hours substring to find where minutes begin.
 *   3. Paint the full string but clip to only the characters that belong to this layer.
 *   4. This guarantees both layers have bit-identical glyph positions.
 */
async function paintClockCanvas(cs, canvasW, canvasH, isFrontLayer) {
  let hStr, mStr, sStr;
  const timeVal = cs.preveiwTime || cs.previewTime;
  if (timeVal) {
    const parts = timeVal.split(":");
    let hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parts[2] ? parseInt(parts[2], 10) : 0;
    if (isNaN(hours)) hours = cs.is24Hr ? 20 : 8;
    if (isNaN(minutes)) minutes = 45;
    if (!cs.is24Hr) hours = hours % 12 || 12;
    hStr = hours.toString().padStart(2, "0");
    mStr = minutes.toString().padStart(2, "0");
    sStr = (isNaN(seconds) ? 0 : seconds).toString().padStart(2, "0");
  } else {
    let hours = cs.is24Hr ? 20 : 8;
    hStr = hours.toString().padStart(2, "0");
    mStr = "45";
    sStr = "00";
  }

  const showMinutes = cs.showMinutes;
  const showSeconds = cs.showSeconds && showMinutes; // seconds only meaningful if minutes shown
  const isHorizontal = cs.showClockHorizontal !== false; // default true

  // ── Step 1: probe at a fixed size to measure digit width for this font ───────
  const probeSize = 100;
  const probeFontFamily = await ensureFont(cs.clockFontFamily || "monospace", probeSize);
  const probeSpec = `${probeSize}px "${probeFontFamily}", monospace`;

  const probeCanvas = document.createElement("canvas");
  probeCanvas.width = 4; probeCanvas.height = 4;
  const pc = probeCanvas.getContext("2d");
  pc.font = probeSpec;
  pc.letterSpacing = "0px";

  const probeDigitW = pc.measureText("0").width;
  const probeGroupW = probeDigitW * 2;

  // Content width always uses 2 groups so font size stays consistent regardless of showMinutes
  const probeContentW = probeGroupW * 1;

  // ── Step 2: scale so 100% clockFontSize = exactly canvasW wide (at line spacing 0) ──
  const maxFontSize = probeSize * canvasW / probeContentW;
  const fontSizePx = Math.round(((cs.clockFontSize ?? 35) / 100) * maxFontSize);
  const fontFamily = probeFontFamily;

  // Use an oversized paint canvas so text + shadows are never clipped
  const paintW = canvasW * 2;
  const paintH = canvasH * 2;

  const canvas = document.createElement("canvas");
  canvas.width = paintW;
  canvas.height = paintH;
  const ctx = canvas.getContext("2d");

  const fontSpec = `${fontSizePx}px "${fontFamily}", monospace`;
  ctx.font = fontSpec;
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  try { ctx.letterSpacing = "0px"; } catch (_) { } // we handle spacing manually for symmetry
  const letterSpacingPx = (cs.clockLetterSpacing ?? 0) * fontSizePx;
  const centerY = paintH / 2;
  let centerX = paintW / 2;

  // Full string drawn as one unit so font spaces all characters (including :) equally
  const fullStr = showMinutes
    ? (cs.showColon ? hStr + ":" + mStr : hStr + mStr)
    : hStr;

  const digitW = ctx.measureText("0").width;

  let hDrawY, mDrawY, sDrawY;
  if (!isHorizontal && showMinutes) {
    // Match ClockLiveWallpaperService's vertical FontMetrics calculation.
    const { lineHeight, paddingCompensation } = androidVerticalMetrics(ctx, `${hStr}${mStr}`);
    const lineSpacingShiftPx = getAndroidLineSpacingShift(
      cs.clockLineSpacing,
      fontSizePx
    );

    const digitMetrics = ctx.measureText("0123456789");
    const digitAscent = Number.isFinite(digitMetrics.actualBoundingBoxAscent)
      ? digitMetrics.actualBoundingBoxAscent
      : 0;
    const digitDescent = Number.isFinite(digitMetrics.actualBoundingBoxDescent)
      ? digitMetrics.actualBoundingBoxDescent
      : 0;
    const androidBaseSeparation = lineHeight - paddingCompensation * 2;
    const minimumVisibleGap = Math.max(1, lineHeight * 0.02);
    const glyphSafeBaseSeparation = digitAscent + digitDescent + minimumVisibleGap;
    const baseSeparation = Math.max(glyphSafeBaseSeparation);
    const rowSeparation = baseSeparation + lineSpacingShiftPx * 2;

    if (showSeconds) {
      // 3 rows: hour at -rowSeparation, minute at 0, second at +rowSeparation (relative to centerY)
      hDrawY = centerY - rowSeparation;
      mDrawY = centerY;
      sDrawY = centerY + rowSeparation;
    } else {
      hDrawY = centerY - rowSeparation / 2;
      mDrawY = centerY + rowSeparation / 2;
      sDrawY = centerY;
    }
  } else {
    hDrawY = centerY;
    mDrawY = centerY;
    sDrawY = centerY;
  }

  // Determine which groups this layer paints
  const drawH = (cs.showHours !== false) && (cs.hoursForward === isFrontLayer);
  const drawM = showMinutes && (cs.minutesForward === isFrontLayer);
  const drawS = showSeconds && (cs.secondsForward === isFrontLayer);
  if (!drawH && !drawM && !drawS) return canvas;

  const shadowScaleX = fontSizePx / 17;
  const shadowScaleY = fontSizePx / 16;

  // Build fill style — gradient spans the full content area
  let fillStyle;
  if (cs.gradientEnabled) {
    const angle = ((cs.gradAngle || 0) - 90) * DEG;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const halfW = digitW * 4 / 2; // matches Android: singleCharW * 4f
    const halfH = fontSizePx / 2;
    const dist = Math.sqrt(halfW * halfW + halfH * halfH);
    const gCx = paintW / 2;
    const gCy = centerY;
    let grad;
    if (cs.gradientType === "radial") {
      grad = ctx.createRadialGradient(gCx, gCy, 0, gCx, gCy, dist);
    } else {
      grad = ctx.createLinearGradient(
        gCx - dist * cos, gCy - dist * sin,
        gCx + dist * cos, gCy + dist * sin
      );
    }
    grad.addColorStop(Math.max(0, cs.gradStop1 ?? 0), cs.clockColor || "#ffffff");
    grad.addColorStop(Math.min(1, cs.gradStop2 ?? 1), cs.clockColor2 || "#ffffff");
    fillStyle = grad;
  } else {
    fillStyle = null; // resolved per-group below
  }

  // Helper: make a temp canvas with matching font (no built-in letterSpacing)
  const makeTmp = () => {
    const t = document.createElement("canvas");
    t.width = paintW; t.height = paintH;
    const c = t.getContext("2d");
    c.font = fontSpec;
    c.textBaseline = "alphabetic";
    c.textAlign = "left";
    try { c.letterSpacing = "0px"; } catch (_) { }
    return c;
  };

  const hexToRgba = (hex, a) => {
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  };

  // Draw str centered at centerX with symmetric letter spacing.
  // Each character expands equally outward from the center — no digit stays fixed.
  const drawStr = (c, str, y) => {
    const n = str.length;
    if (n === 0) return;
    c.textAlign = "left";
    // Measure each character
    const widths = [];
    let totalGlyphW = 0;
    for (let i = 0; i < n; i++) {
      const w = c.measureText(str[i]).width;
      widths.push(w);
      totalGlyphW += w;
    }
    // Total width with spacing between characters
    const totalW = totalGlyphW + (n - 1) * letterSpacingPx;
    // Start position so the string is centered at centerX
    let x = centerX - totalW / 2;
    const baselineY = androidCenteredBaseline(c, str, y);
    for (let i = 0; i < n; i++) {
      c.fillText(str[i], x, baselineY);
      x += widths[i] + letterSpacingPx;
    }
  };
  const strokeStr = (c, str, y) => {
    const n = str.length;
    if (n === 0) return;
    c.textAlign = "left";
    const widths = [];
    let totalGlyphW = 0;
    for (let i = 0; i < n; i++) {
      const w = c.measureText(str[i]).width;
      widths.push(w);
      totalGlyphW += w;
    }
    const totalW = totalGlyphW + (n - 1) * letterSpacingPx;
    let x = centerX - totalW / 2;
    const baselineY = androidCenteredBaseline(c, str, y);
    for (let i = 0; i < n; i++) {
      c.strokeText(str[i], x, baselineY);
      x += widths[i] + letterSpacingPx;
    }
  };

  // Draw a string with shadow + border + fill pipeline
  const drawGroup = (str, groupY, fillColor, onlyShadow = false, groupType = "full") => {
    // 1. Shadow
    if (cs.shadowEnabled) {
      const sc = makeTmp();
      const offX = 10000; // Off-screen projection trick
      sc.shadowColor = hexToRgba(cs.shadowColor || "#000000", cs.shadowOpacity ?? 1);
      sc.shadowOffsetX = ((cs.shadowHOffset || 0) * shadowScaleX) + offX;
      sc.shadowOffsetY = (cs.shadowVOffset || 0) * shadowScaleY;
      sc.shadowBlur = ((cs.shadowBlur || 0) * shadowScaleY) / 2;
      sc.fillStyle = "#ffffff";

      sc.translate(-offX, 0);
      drawStr(sc, str, groupY);
      sc.translate(offX, 0); // Restore context

      ctx.drawImage(sc.canvas, 0, 0);
    }

    if (onlyShadow) return;

    const targets = Array.isArray(cs.clockBorderTarget) && cs.clockBorderTarget.length > 0
      ? cs.clockBorderTarget
      : ["Full"];
    const hasHours = targets.includes("Full") || targets.includes("Hours");
    const hasMinutes = targets.includes("Full") || targets.includes("Minutes");
    const hasSeconds = targets.includes("Full") || targets.includes("Seconds");

    let isTarget = targets.includes("Full");
    if (groupType === "hours") isTarget = hasHours;
    else if (groupType === "minutes") isTarget = hasMinutes;
    else if (groupType === "seconds") isTarget = hasSeconds;
    else if (groupType === "colon_hm" || groupType === "colon") isTarget = targets.includes("Full") || (hasHours && hasMinutes);
    else if (groupType === "colon_ms") isTarget = targets.includes("Full") || (hasMinutes && hasSeconds);

    const hasBorder = Boolean(cs.borderEnabled && isTarget);

    // 2. Border (outside-only)
    if (hasBorder) {
      const bc = makeTmp();
      bc.strokeStyle = cs.borderColor || "#ffffff";
      const bw = cs.borderWidth || 1;
      const effectiveBw = bw < 1.0 ? 0.30 + Math.max(0, bw - 0.10) * (0.70 / 0.90) : bw;
      bc.lineWidth = effectiveBw * 18 * (canvasW / 1080);
      bc.lineJoin = "round";
      strokeStr(bc, str, groupY);
      bc.globalCompositeOperation = "destination-out";
      bc.fillStyle = "rgba(0,0,0,1)";
      drawStr(bc, str, groupY);
      ctx.globalAlpha = cs.borderOpacity ?? 1;
      ctx.drawImage(bc.canvas, 0, 0);
      ctx.globalAlpha = 1;
    }

    // 3. Fill (drawn if no border on this group, or if fillBorderEnabled is true)
    if (!hasBorder || cs.fillBorderEnabled) {
      const fc = makeTmp();
      fc.fillStyle = fillStyle || fillColor;
      drawStr(fc, str, groupY);
      ctx.globalAlpha = cs.clockOpacity / 100;
      ctx.drawImage(fc.canvas, 0, 0);
      ctx.globalAlpha = 1;
    }
  };

  // Helper to measure exact rendered width of a group including letter spacing
  const getGroupW = (str) => {
    const n = str.length;
    if (n === 0) return 0;
    let totalGlyphW = 0;
    for (let i = 0; i < n; i++) {
      totalGlyphW += ctx.measureText(str[i]).width;
    }
    return totalGlyphW + (n - 1) * letterSpacingPx;
  };

  // Vertical: draw hStr, mStr, sStr independently, each centered at their Y
  // Horizontal with minutes: position groups so spacing between groups matches digit spacing
  if (!isHorizontal && showMinutes && cs.showHours !== false) {
    if (cs.shadowEnabled) {
      if (drawH) drawGroup(hStr, hDrawY, null, true, "hours");
      if (drawM) drawGroup(mStr, mDrawY, null, true, "minutes");
      if (drawS) drawGroup(sStr, sDrawY, null, true, "seconds");
    }
    const hasShadow = cs.shadowEnabled;
    cs.shadowEnabled = false;
    if (drawH) drawGroup(hStr, hDrawY, cs.clockColor || "#ffffff", false, "hours");
    if (drawM) drawGroup(mStr, mDrawY, cs.clockColor2 || "#ffffff", false, "minutes");
    if (drawS) drawGroup(sStr, sDrawY, cs.clockColor2 || "#ffffff", false, "seconds");
    cs.shadowEnabled = hasShadow;
  } else if (isHorizontal && showMinutes && cs.showHours !== false) {
    const totalW_h = getGroupW(hStr);
    const totalW_m = getGroupW(mStr);
    const totalW_s = showSeconds ? getGroupW(sStr) : 0;
    const gap = getAndroidLineSpacingShift(cs.clockLineSpacing, fontSizePx);

    if (cs.showColon) {
      const colonW = getGroupW(":");
      const sep = colonW / 2 + letterSpacingPx + gap / 2;
      let cxH, cxM, cxS;
      if (showSeconds) {
        // h : m : s — center the whole group
        const totalSpan = totalW_h + sep + totalW_m + sep + totalW_s + colonW;
        const startX = paintW / 2 - totalSpan / 2;
        cxH = startX + totalW_h / 2;
        cxM = startX + totalW_h + colonW + letterSpacingPx * 2 + gap + totalW_m / 2;
        cxS = startX + totalW_h + colonW + letterSpacingPx * 2 + gap + totalW_m + colonW + letterSpacingPx * 2 + gap + totalW_s / 2;
      } else {
        cxH = paintW / 2 - sep - totalW_h / 2;
        cxM = paintW / 2 + sep + totalW_m / 2;
      }
      if (cs.shadowEnabled) {
        if (drawH) { centerX = cxH; drawGroup(hStr, centerY, null, true, "hours"); }
        centerX = showSeconds ? cxH + totalW_h / 2 + sep : paintW / 2;
        drawGroup(":", centerY, null, true, "colon_hm");
        if (drawM) { centerX = cxM; drawGroup(mStr, centerY, null, true, "minutes"); }
        if (showSeconds) {
          centerX = cxM + totalW_m / 2 + sep;
          drawGroup(":", centerY, null, true, "colon_ms");
          if (drawS) { centerX = cxS; drawGroup(sStr, centerY, null, true, "seconds"); }
        }
      }
      const hasShadow = cs.shadowEnabled;
      cs.shadowEnabled = false;
      if (drawH) { centerX = cxH; drawGroup(hStr, centerY, cs.clockColor || "#ffffff", false, "hours"); }
      centerX = showSeconds ? cxH + totalW_h / 2 + sep : paintW / 2;
      drawGroup(":", centerY, cs.clockColor || "#ffffff", false, "colon_hm");
      if (drawM) { centerX = cxM; drawGroup(mStr, centerY, cs.clockColor2 || "#ffffff", false, "minutes"); }
      if (showSeconds) {
        centerX = cxM + totalW_m / 2 + sep;
        drawGroup(":", centerY, cs.clockColor || "#ffffff", false, "colon_ms");
        if (drawS) { centerX = cxS; drawGroup(sStr, centerY, cs.clockColor2 || "#ffffff", false, "seconds"); }
      }
      cs.shadowEnabled = hasShadow;
    } else {
      const groupGap = gap;
      let cxH, cxM, cxS;
      if (showSeconds) {
        const totalSpan = totalW_h + groupGap + totalW_m + groupGap + totalW_s;
        const startX = paintW / 2 - totalSpan / 2;
        cxH = startX + totalW_h / 2;
        cxM = startX + totalW_h + groupGap + totalW_m / 2;
        cxS = startX + totalW_h + groupGap + totalW_m + groupGap + totalW_s / 2;
      } else {
        cxH = paintW / 2 - (totalW_m + groupGap) / 2;
        cxM = paintW / 2 + (totalW_h + groupGap) / 2;
      }
      if (cs.shadowEnabled) {
        if (drawH) { centerX = cxH; drawGroup(hStr, centerY, null, true, "hours"); }
        if (drawM) { centerX = cxM; drawGroup(mStr, centerY, null, true, "minutes"); }
        if (drawS) { centerX = cxS; drawGroup(sStr, centerY, null, true, "seconds"); }
      }
      const hasShadow = cs.shadowEnabled;
      cs.shadowEnabled = false;
      if (drawH) { centerX = cxH; drawGroup(hStr, centerY, cs.clockColor || "#ffffff", false, "hours"); }
      if (drawM) { centerX = cxM; drawGroup(mStr, centerY, cs.clockColor2 || "#ffffff", false, "minutes"); }
      if (drawS) { centerX = cxS; drawGroup(sStr, centerY, cs.clockColor2 || "#ffffff", false, "seconds"); }
      cs.shadowEnabled = hasShadow;
    }
    centerX = paintW / 2;
  } else if (cs.showHours === false) {
    if (cs.shadowEnabled) {
      if (drawM) drawGroup(mStr, centerY, null, true, "minutes");
    }
    const hasShadow = cs.shadowEnabled;
    cs.shadowEnabled = false;
    if (drawM) drawGroup(mStr, centerY, cs.clockColor2 || "#ffffff", false, "minutes");
    cs.shadowEnabled = hasShadow;
  } else {
    const fallbackType = showMinutes ? "full" : "hours";
    if (cs.shadowEnabled) {
      if (drawH || drawM) drawGroup(fullStr, hDrawY, null, true, fallbackType);
    }
    const hasShadow = cs.shadowEnabled;
    cs.shadowEnabled = false;
    if (drawH || drawM) drawGroup(fullStr, hDrawY, cs.clockColor || "#ffffff", false, fallbackType);
    cs.shadowEnabled = hasShadow;
  }

  return canvas;
}


// ─── Date canvas ──────────────────────────────────────────────────────────────

async function paintDateCanvas(cs, canvasW, canvasH) {
  const canvas = document.createElement("canvas");
  canvas.width = canvasW;
  canvas.height = canvasH;
  if (cs.dateEnabled === false) return canvas;

  const ctx = canvas.getContext("2d");

  const probeSize = 100;
  const dateFontFamily = await ensureFont(cs.dateFontFamily || "Barlow Bold", probeSize);
  const probeSpec = `${probeSize}px "${dateFontFamily}", monospace`;
  const probeCanvas = document.createElement("canvas");
  probeCanvas.width = 4; probeCanvas.height = 4;
  const pc = probeCanvas.getContext("2d");
  pc.font = probeSpec;
  pc.letterSpacing = "0px";
  const singleCharW = pc.measureText("0").width;
  // Same formula as clock but *10 instead of *4 → date is 3x smaller than clock
  const maxFontSize = probeSize * canvasW / (singleCharW * 10);
  const dateFontSizePx = Math.max(0.1, Math.round((cs.dateFontSize ?? 40) / 100 * maxFontSize));

  ctx.font = `${dateFontSizePx}px "${dateFontFamily}", monospace`;
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "center";
  ctx.fillStyle = cs.dateColor || "#ffffff";
  ctx.globalAlpha = (cs.dateOpacity ?? 100) / 100;

  const x = ((cs.dateHPos ?? 50) / 100) * canvasW;
  const y = ((cs.dateVPos ?? 16) / 100) * canvasH;
  let targetDate = new Date();
  const dateVal = cs.previewDate || cs.preveiwDate;
  if (dateVal) {
    const parts = dateVal.split("-").map(Number);
    if (parts.length === 3 && !parts.some(isNaN)) {
      targetDate = new Date(parts[0], parts[1] - 1, parts[2]);
    }
  }
  const dateText = buildDateStr(cs.dateFormat || "Day Date Month", targetDate);
  const baselineY = androidCenteredBaseline(ctx, dateText, 0);

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((cs.dateRotation || 0) * DEG);
  ctx.fillText(dateText, 0, baselineY);
  ctx.restore();

  return canvas;
}

// ─── Texture creation ─────────────────────────────────────────────────────────

function canvasToTexture(canvas) {
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// ─── Mesh builder ─────────────────────────────────────────────────────────────

function buildFullCanvasMesh(texture, canvasW, canvasH, renderOrder) {
  const geo = new THREE.PlaneGeometry(canvasW, canvasH);
  const mat = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.renderOrder = renderOrder;
  return mesh;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Creates the Two clock layer meshes (back + front) and the date mesh.
 * Returns { backMesh, frontMesh, dateMesh, dispose }.
 *
 * These are full-canvas transparent planes so their glyph positions are
 * absolutely identical regardless of transform origin.
 * Transforms are applied to a shared GROUP so back and front move together.
 */
export async function createClockLayers(cs, canvasW, canvasH) {
  const [backCanvas, frontCanvas, dateCanvas] = await Promise.all([
    paintClockCanvas(cs, canvasW, canvasH, false),
    paintClockCanvas(cs, canvasW, canvasH, true),
    paintDateCanvas(cs, canvasW, canvasH),
  ]);

  const backTex = canvasToTexture(backCanvas);
  const frontTex = canvasToTexture(frontCanvas);
  const dateTex = canvasToTexture(dateCanvas);

  const backMesh = buildFullCanvasMesh(backTex, canvasW * 2, canvasH * 2, 10);
  const frontMesh = buildFullCanvasMesh(frontTex, canvasW * 2, canvasH * 2, 40);
  const dateMesh = buildFullCanvasMesh(dateTex, canvasW, canvasH, 20);

  // Apply the clock transform (position + rotation + scale + skew) to back+front
  // Date gets its own independent position (already baked into the canvas texture).
  applyClockTransform(backMesh, cs, canvasW, canvasH);
  applyClockTransform(frontMesh, cs, canvasW, canvasH);
  applySkewToMatrix(backMesh, cs);
  applySkewToMatrix(frontMesh, cs);

  // Date mesh stays at center (its position is baked into the texture)
  dateMesh.position.set(0, 0, 0);

  return {
    backMesh,
    frontMesh,
    dateMesh,
    dispose() {
      [backTex, frontTex, dateTex].forEach((t) => t.dispose());
      [backMesh, frontMesh, dateMesh].forEach((m) => {
        m.geometry.dispose();
        m.material.dispose();
      });
    },
  };
}

/**
 * Re-renders clock canvases and updates textures in place (no mesh recreation).
 */
export async function updateClockLayers(layers, cs, canvasW, canvasH) {
  if (!layers) return;

  const [backCanvas, frontCanvas, dateCanvas] = await Promise.all([
    paintClockCanvas(cs, canvasW, canvasH, false),
    paintClockCanvas(cs, canvasW, canvasH, true),
    paintDateCanvas(cs, canvasW, canvasH),
  ]);

  updateTexture(layers.backMesh, backCanvas);
  updateTexture(layers.frontMesh, frontCanvas);
  updateTexture(layers.dateMesh, dateCanvas);

  updateClockLayerTransforms(layers, cs, canvasW, canvasH);
}

/**
 * Updates settings that only change the clock meshes' matrices. Keeping this
 * separate avoids repainting and re-uploading the large text canvases while a
 * transform slider is being dragged.
 */
export function updateClockLayerTransforms(layers, cs, canvasW, canvasH) {
  if (!layers) return;

  // Re-enable auto update so applyClockTransform writes fresh position/rotation/scale
  layers.backMesh.matrixAutoUpdate = true;
  layers.frontMesh.matrixAutoUpdate = true;

  applyClockTransform(layers.backMesh, cs, canvasW, canvasH);
  applyClockTransform(layers.frontMesh, cs, canvasW, canvasH);
  applySkewToMatrix(layers.backMesh, cs);
  applySkewToMatrix(layers.frontMesh, cs);
}

function updateTexture(mesh, canvas) {
  if (!mesh) return;
  const texture = mesh.material.map;
  if (texture) {
    texture.image = canvas;
    texture.needsUpdate = true;
    return;
  }

  mesh.material.map = canvasToTexture(canvas);
  mesh.material.needsUpdate = true;
}